import { ContentItemFragmentFragment, NewsFeedItemGroupFragment } from '@lopo-tech/lopo-api'
import { throttle } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PostPreviewPlaceholder from 'src/components/content_placeholder/components/post_preview/post_preview'
import EmptyFeed from 'src/components/feed/empty_feed/empty_feed'
import Masonry from 'src/components/feed/layouts/masonry_feed/masonry/masonry'
import OpenGroupButton from 'src/components/post/open_group_button/open_group_button'
import PostPreview from 'src/components/post/preview/post_preview'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'
import { useResizeListener } from 'src/hooks_shared/use_event_listener'
import { textResources } from 'src/lang/de'
import { readColumnCountFromDOM } from 'src/utils/column_count'

export interface IFeedProps {
  isLoading: boolean;
  items: NewsFeedItemGroupFragment[] | ContentItemFragmentFragment[]
  renderPostCreate?: () => React.ReactNode;
  showEmptyFeed?: boolean;
  onVisibleChange?: (id: string, inView: boolean) => void
}

interface IProps extends IFeedProps {
  extraElements?: Array<JSX.Element | undefined>;
  withBorder?: boolean;
}

const areNewsfeedItemGroup = (items: NewsFeedItemGroupFragment[] | ContentItemFragmentFragment[]): items is NewsFeedItemGroupFragment[] => {
  return !!items.length && items[0].__typename === 'NewsfeedItemGroup'
}
const MasonryFeed: React.FC<IProps> = ({
  items,
  extraElements,
  isLoading,
  renderPostCreate,
  onVisibleChange,
  showEmptyFeed = true,
  withBorder = false
}) => {
  const [columnCount, setColumnCount] = useState(() => readColumnCountFromDOM())
  const listener = useCallback(
    throttle(() => {
      setColumnCount(readColumnCountFromDOM())
    }, 100),
    []
  )

  useEffect(() => {
    setColumnCount(readColumnCountFromDOM())
  }, [])

  useResizeListener(listener, { passive: true })

  const isOnMobileBreakpoint = columnCount === 1

  const tilesList = useMemo(() => {
    const tiles = []

    if (renderPostCreate && !isOnMobileBreakpoint) { tiles.push(renderPostCreate()) }

    tiles.push(...extraElements ?? [])

    if (areNewsfeedItemGroup(items)) {
      tiles.push(...renderItemGroups(items, onVisibleChange, withBorder))
    } else {
      tiles.push(...items.map((item) => renderContentItem(item, withBorder)))
    }

    if (!isLoading && !items?.length && showEmptyFeed) {
      tiles.push(<EmptyFeed key='empty-feed' />)
    }

    if (isLoading) {
      tiles.push(...renderPostsPlaceholder)
    }
    return tiles
  },
  [items, extraElements, isLoading, isOnMobileBreakpoint, renderPostCreate, showEmptyFeed]
  )

  return (
    <section>
      {renderPostCreate && isOnMobileBreakpoint && renderPostCreate()}
      <Masonry>
        {tilesList}
      </Masonry>
    </section>
  )
}

const renderPostsPlaceholder: JSX.Element[] = [
  <PostPreviewPlaceholder key='masonry-1'/>,
  <PostPreviewPlaceholder key='masonry-2'/>,
  <PostPreviewPlaceholder key='masonry-3'/>
]

const renderItemGroups = (
  itemGroups: NewsFeedItemGroupFragment[],
  onVisibleChange?: (id: string, inView: boolean) => void,
  withBorder?: boolean
): JSX.Element[] => {
  return itemGroups.map((group) => (
    <ItemGroup key={group.id} itemGroup={group} onVisibleChange={onVisibleChange} withBorder={withBorder}/>
  ))
}

const renderContentItem = (item: ContentItemFragmentFragment, withBorder?: boolean) => {
  return <PostPreview key={item.id} {...item} withBorder={withBorder} />
}

export default MasonryFeed

type ItemGroupProps = {
  itemGroup: NonNullable<NewsFeedItemGroupFragment>;
  onVisibleChange?: (id: string, inView: boolean) => void;
  withBorder?: boolean;
};

const ItemGroup: React.FC<ItemGroupProps> = ({ itemGroup, onVisibleChange, withBorder }) => {
  const [collapsed, setCollapsed] = useState(true)

  return (
    <React.Fragment key={'itemGroup' + itemGroup.id}>
      <UserOnboardingCTA
        active={!itemGroup.items[0].contentItem.canSeeFullContent.value}
        gtmSourceName="feed"
      >
        {collapsed
          ? (
            <PostPreview
              {...itemGroup.items[0].contentItem}
              inclusionReason={itemGroup.items[0].inclusionReason?.reason}
              onVisibleChange={onVisibleChange}
              withBorder={withBorder}
            >
              {itemGroup.items?.length > 1 && (
                <OpenGroupButton
                  icon={'arrow-down'}
                  label={textResources.newsfeed.moreItemsInPostGroup(
                    itemGroup.items.length - 1
                  )}
                  onClick={() => setCollapsed(false)}
                />
              )}
            </PostPreview>
          )
          : (
            itemGroup.items.map((item) => (
              <PostPreview
                {...item.contentItem}
                key={item.contentItem.id}
                inclusionReason={item.inclusionReason?.reason}
                onVisibleChange={onVisibleChange}
                withBorder={withBorder}
              />
            ))
          )}
      </UserOnboardingCTA>
    </React.Fragment>
  )
}
